/**
 * Text-Search Highlight Utility
 *
 * Finds chunk text within a rendered document DOM and wraps matches in <mark> elements.
 * Used by DocxViewer and HtmlDocumentViewer for non-PDF citation highlighting.
 */

/** Normalize whitespace: collapse runs of whitespace to single space, trim */
function normalize(text: string): string {
  return text.replace(/\s+/g, ' ').trim()
}

/**
 * Normalize field-separator commas to spaces, preserving numeric commas (e.g. $425,000).
 * A comma between two digits is considered numeric; all others are field separators.
 */
function normalizeFieldCommas(text: string): string {
  return text.replace(/,(?!\d)/g, ' ').replace(/(?<!\d),/g, ' ')
}

/**
 * Replace CSV-style quote characters with spaces while preserving string length.
 * This lets us keep index mapping stable back to original text offsets.
 */
function normalizeCsvQuotes(text: string): string {
  return text.replace(/"/g, ' ')
}

/** Tags whose text content should be skipped (CSS rules, scripts, etc.) */
const SKIP_TAGS = new Set(['STYLE', 'SCRIPT', 'NOSCRIPT'])

/** Block-level tags that represent visual paragraph/cell boundaries */
const BLOCK_TAGS = new Set([
  'P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
  'LI', 'TD', 'TH', 'TR', 'TABLE', 'SECTION', 'ARTICLE',
  'BLOCKQUOTE', 'PRE', 'HEADER', 'FOOTER', 'FIGCAPTION',
])

/** Find the nearest block-level ancestor of a node (within container) */
function getBlockAncestor(node: Node, container: Node): Element | null {
  let current = node.parentElement
  while (current && current !== container) {
    if (BLOCK_TAGS.has(current.tagName)) return current
    current = current.parentElement
  }
  return null
}

/**
 * Walk visible text nodes in a container, skipping <style>/<script>.
 * Inserts virtual space separators between different block-level elements
 * so that "Hello</p><p>World" produces "Hello World" instead of "HelloWorld".
 * Returns text nodes with cumulative offsets (including virtual spaces) for range mapping.
 */
function collectTextNodes(container: Node): Array<{ node: Text; offset: number }> {
  const result: Array<{ node: Text; offset: number }> = []
  let offset = 0

  const ownerDoc = (container as Element).ownerDocument ?? document
  const walker = ownerDoc.createTreeWalker(container, NodeFilter.SHOW_TEXT)

  let prevBlockAncestor: Element | null = null
  let prevParent: Element | null = null
  let node: Text | null
  while ((node = walker.nextNode() as Text | null)) {
    // Skip text inside <style>, <script>, etc.
    if (node.parentElement && SKIP_TAGS.has(node.parentElement.tagName)) continue

    // Add virtual space when crossing block-level element boundaries
    const blockAncestor = getBlockAncestor(node, container)
    if (prevBlockAncestor && blockAncestor && blockAncestor !== prevBlockAncestor) {
      offset += 1
    } else if (prevParent && node.parentElement && node.parentElement !== prevParent) {
      // Add virtual space between sibling inline elements (e.g., PDF text layer spans)
      offset += 1
    }

    result.push({ node, offset })
    offset += node.textContent?.length ?? 0
    prevBlockAncestor = blockAncestor
    prevParent = node.parentElement
  }

  return result
}

/**
 * Build a normalized version of the full text, tracking how normalized positions
 * map back to original positions.
 */
function buildNormalizedMap(fullText: string): { normalized: string; originalIndices: number[] } {
  const normalized: string[] = []
  const originalIndices: number[] = []
  let inWhitespace = false

  for (let i = 0; i < fullText.length; i++) {
    const ch = fullText[i]!
    if (/\s/.test(ch)) {
      if (!inWhitespace && normalized.length > 0) {
        normalized.push(' ')
        originalIndices.push(i)
        inWhitespace = true
      }
    } else {
      normalized.push(ch)
      originalIndices.push(i)
      inWhitespace = false
    }
  }

  return { normalized: normalized.join(''), originalIndices }
}

/**
 * Find the best match position of snippet within the container's text.
 * Returns original-text start and end indices, or null if no match found.
 */
function findMatchRange(
  fullText: string,
  snippet: string,
  sectionHeader: string | null
): { start: number; end: number } | null {
  const { normalized: normFull, originalIndices } = buildNormalizedMap(fullText)
  const normSnippet = normalize(snippet)

  if (!normSnippet) return null

  // Strategy 1: Exact normalized match
  const exactMatches = findAllOccurrences(normFull, normSnippet)
  if (exactMatches.length === 1) {
    const normStart = exactMatches[0]!
    const normEnd = normStart + normSnippet.length - 1
    return {
      start: originalIndices[normStart] ?? 0,
      end: (originalIndices[normEnd] ?? originalIndices[normEnd - 1] ?? 0) + 1,
    }
  }

  if (exactMatches.length > 1) {
    // Disambiguate: prefer match closest to sectionHeader
    const picked = disambiguateBySection(normFull, exactMatches, normSnippet.length, sectionHeader, originalIndices)
    return picked
  }

  // Strategy 2: Comma-normalized match (Excel CSV chunks vs HTML table cells)
  const commaSnippet = normalize(normalizeFieldCommas(normSnippet))
  if (commaSnippet) {
    const commaMap = buildNormalizedMap(normalizeFieldCommas(fullText))
    const commaMatches = findAllOccurrences(commaMap.normalized, commaSnippet)
    if (commaMatches.length === 1) {
      const normStart = commaMatches[0]!
      const normEnd = normStart + commaSnippet.length - 1
      return {
        start: commaMap.originalIndices[normStart] ?? 0,
        end: (commaMap.originalIndices[normEnd] ?? commaMap.originalIndices[normEnd - 1] ?? 0) + 1,
      }
    }
    if (commaMatches.length > 1) {
      return disambiguateBySection(commaMap.normalized, commaMatches, commaSnippet.length, sectionHeader, commaMap.originalIndices)
    }
  }

  // Strategy 3: CSV quoted-field normalization (e.g. "Road, Bridge",500000)
  const quotedCsvSnippet = normalize(normalizeFieldCommas(normalizeCsvQuotes(normSnippet)))
  if (quotedCsvSnippet) {
    const quotedCsvMap = buildNormalizedMap(
      normalizeFieldCommas(normalizeCsvQuotes(fullText))
    )
    const quotedCsvMatches = findAllOccurrences(quotedCsvMap.normalized, quotedCsvSnippet)
    if (quotedCsvMatches.length === 1) {
      const normStart = quotedCsvMatches[0]!
      const normEnd = normStart + quotedCsvSnippet.length - 1
      return {
        start: quotedCsvMap.originalIndices[normStart] ?? 0,
        end: (quotedCsvMap.originalIndices[normEnd] ?? quotedCsvMap.originalIndices[normEnd - 1] ?? 0) + 1,
      }
    }
    if (quotedCsvMatches.length > 1) {
      return disambiguateBySection(
        quotedCsvMap.normalized,
        quotedCsvMatches,
        quotedCsvSnippet.length,
        sectionHeader,
        quotedCsvMap.originalIndices
      )
    }

    // Strategy 4: Strong-prefix fallback for near-miss long CSV/table snippets
    const prefixFallback = findPrefixFallbackRange(
      quotedCsvMap.normalized,
      quotedCsvSnippet,
      sectionHeader,
      quotedCsvMap.originalIndices
    )
    if (prefixFallback) return prefixFallback
  }

  // Strategy 5: Anchor match — first ~150 and last ~150 chars
  const anchorLen = Math.min(150, Math.floor(normSnippet.length / 2))
  if (anchorLen > 30) {
    const head = normSnippet.slice(0, anchorLen)
    const tail = normSnippet.slice(-anchorLen)
    const headIdx = normFull.indexOf(head)
    const tailIdx = normFull.lastIndexOf(tail)

    if (headIdx !== -1 && tailIdx !== -1 && tailIdx >= headIdx) {
      const normEnd = tailIdx + tail.length - 1
      return {
        start: originalIndices[headIdx] ?? 0,
        end: (originalIndices[normEnd] ?? originalIndices[normEnd - 1] ?? 0) + 1,
      }
    }
  }

  // Strategy 6: Graceful degradation — no match found
  return null
}

/** Find all occurrences of needle in haystack */
function findAllOccurrences(haystack: string, needle: string): number[] {
  const results: number[] = []
  let pos = 0
  while (pos < haystack.length) {
    const idx = haystack.indexOf(needle, pos)
    if (idx === -1) break
    results.push(idx)
    pos = idx + 1
  }
  return results
}

/**
 * Fallback for long snippets that almost match but diverge in the tail.
 * Tries progressively shorter prefixes and returns the first strong hit.
 */
function findPrefixFallbackRange(
  normFull: string,
  normSnippet: string,
  sectionHeader: string | null,
  originalIndices: number[]
): { start: number; end: number } | null {
  const maxPrefix = Math.min(normSnippet.length, 500)
  const minPrefix = 80
  if (maxPrefix < minPrefix) return null

  for (let prefixLen = maxPrefix; prefixLen >= minPrefix; prefixLen -= 20) {
    const prefix = normSnippet.slice(0, prefixLen).trimEnd()
    if (prefix.length < minPrefix) continue

    const matches = findAllOccurrences(normFull, prefix)
    if (matches.length === 0) continue

    if (matches.length === 1) {
      const normStart = matches[0]!
      const normEnd = normStart + prefix.length - 1
      return {
        start: originalIndices[normStart] ?? 0,
        end: (originalIndices[normEnd] ?? originalIndices[normEnd - 1] ?? 0) + 1,
      }
    }

    return disambiguateBySection(normFull, matches, prefix.length, sectionHeader, originalIndices)
  }

  return null
}

/** Pick the match closest to the sectionHeader text, or first match */
function disambiguateBySection(
  normFull: string,
  matchPositions: number[],
  snippetLength: number,
  sectionHeader: string | null,
  originalIndices: number[]
): { start: number; end: number } | null {
  if (!matchPositions.length) return null

  let bestPos = matchPositions[0]!

  if (sectionHeader) {
    const normHeader = normalize(sectionHeader)
    const headerIdx = normFull.indexOf(normHeader)
    if (headerIdx !== -1) {
      // Pick the match closest to (and after) the header
      let closestDist = Infinity
      for (const pos of matchPositions) {
        const dist = pos >= headerIdx ? pos - headerIdx : Infinity
        if (dist < closestDist) {
          closestDist = dist
          bestPos = pos
        }
      }
    }
  }

  const normEnd = bestPos + snippetLength - 1
  return {
    start: originalIndices[bestPos] ?? 0,
    end: (originalIndices[normEnd] ?? originalIndices[normEnd - 1] ?? 0) + 1,
  }
}

/**
 * Wrap the range [start, end) of the original text in <mark> elements.
 * Walks text nodes and splits them as necessary.
 */
function wrapRange(
  textNodes: Array<{ node: Text; offset: number }>,
  start: number,
  end: number,
  doc: Document
): HTMLElement | null {
  let firstMark: HTMLElement | null = null

  for (const { node, offset } of textNodes) {
    const nodeLen = node.textContent?.length ?? 0
    const nodeEnd = offset + nodeLen

    // Skip nodes outside the range
    if (nodeEnd <= start || offset >= end) continue

    const rangeStart = Math.max(0, start - offset)
    const rangeEnd = Math.min(nodeLen, end - offset)

    // Split to isolate the matched portion
    const range = doc.createRange()
    range.setStart(node, rangeStart)
    range.setEnd(node, rangeEnd)

    const mark = doc.createElement('mark')
    mark.style.backgroundColor = 'rgba(250, 204, 21, 0.4)'
    mark.style.borderRadius = '2px'
    mark.style.padding = '1px 0'

    try {
      range.surroundContents(mark)
      if (!firstMark) firstMark = mark
    } catch {
      // surroundContents can fail if range spans partial elements — skip gracefully
    }
  }

  return firstMark
}

/**
 * Highlight matching text in a DOM element using text-search.
 *
 * @param container - The DOM element to search within
 * @param chunkText - The text snippet to find and highlight
 * @param sectionHeader - Optional section header for disambiguation
 * @param scrollIntoView - Whether to scroll to the first match
 */
export function highlightTextInDOM(
  container: HTMLElement,
  chunkText: string | null | undefined,
  sectionHeader?: string | null,
  scrollIntoView: boolean = true
): void {
  if (!chunkText || !container) return

  const textNodes = collectTextNodes(container)
  if (textNodes.length === 0) return

  // Build full text from text nodes, filling virtual separator gaps with spaces
  let fullText = ''
  for (const { node, offset } of textNodes) {
    while (fullText.length < offset) fullText += ' '
    fullText += node.textContent ?? ''
  }
  const matchRange = findMatchRange(fullText, chunkText, sectionHeader ?? null)

  if (!matchRange) return // Graceful degradation: show preview without highlight

  const ownerDoc = container.ownerDocument ?? document
  const firstMark = wrapRange(textNodes, matchRange.start, matchRange.end, ownerDoc)

  if (firstMark && scrollIntoView) {
    // Use requestAnimationFrame to ensure DOM has settled
    requestAnimationFrame(() => {
      firstMark.scrollIntoView({ behavior: 'instant' as ScrollBehavior, block: 'center' })
    })
  }
}

const SEGMENT_MAX_WORDS = 12
const SEGMENT_MIN_WORDS = 4

/**
 * Find the longest contiguous run of the quote's words (verbatim, normalized
 * whitespace) present in the container's text. Fallback tier for PDF table /
 * bullet content where pdf.js reorders the text layer so neither the full
 * quote nor whole sentences match. Returns the matched segment (original
 * casing, single-spaced) or null. Honest by construction: only a verbatim
 * sub-span of the quote is ever returned — never a synthesized region.
 */
export function findLongestQuoteSegment(container: HTMLElement, quote: string): string | null {
  if (!quote || !container) return null

  const textNodes = collectTextNodes(container)
  if (textNodes.length === 0) return null

  let fullText = ''
  for (const { node, offset } of textNodes) {
    while (fullText.length < offset) fullText += ' '
    fullText += node.textContent ?? ''
  }
  const { normalized } = buildNormalizedMap(fullText)

  const words = normalize(quote).split(' ').filter((w) => w.length > 0)
  const maxWindow = Math.min(SEGMENT_MAX_WORDS, words.length)
  for (let n = maxWindow; n >= SEGMENT_MIN_WORDS; n--) {
    for (let i = 0; i + n <= words.length; i++) {
      const segment = words.slice(i, i + n).join(' ')
      if (normalized.includes(segment)) return segment
    }
  }
  return null
}

/**
 * Remove all <mark> highlight elements from a container
 */
export function clearHighlightsInDOM(container: HTMLElement): void {
  const marks = container.querySelectorAll('mark')
  marks.forEach((mark) => {
    const parent = mark.parentNode
    if (parent) {
      while (mark.firstChild) {
        parent.insertBefore(mark.firstChild, mark)
      }
      parent.removeChild(mark)
      parent.normalize()
    }
  })
}
